import React, { useEffect, useMemo, useState } from 'react';
import { ArrowUpRight, FileText, Lock, Search, SearchX } from 'lucide-react';
import { usefulLinksData } from '../data/usefulLinks';
import { Badge } from '../components/ui/Badge';
import { EmptyState } from '../components/ui/misc';
import { normalize } from '../lib/text';

const hostOf = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch (e) {
    return url;
  }
};

// Hosts that only resolve on the hospital network.
const isIntranet = (url) => /\.(local|internal|lan)(:\d+)?(\/|$)/i.test(hostOf(url)) || /^https?:\/\/[^/.]+(\/|$)/i.test(url);
const isPdf = (url) => /\.pdf(\?|#|$)/i.test(url);

/** Curated links (formerly the "Useful links" panel), searchable and all visible. */
export default function ResourcesPage() {
  const [query, setQuery] = useState('');
  useEffect(() => {
    document.title = 'Resources – TCM Search';
    return () => { document.title = 'TCM Search'; };
  }, []);

  const categories = useMemo(() => {
    const q = normalize(query);
    return usefulLinksData
      .map((cat) => ({
        ...cat,
        links: q
          ? cat.links.filter((l) => normalize(`${l.name} ${hostOf(l.url)} ${cat.category}`).includes(q))
          : cat.links,
      }))
      .filter((cat) => cat.links.length > 0);
  }, [query]);

  const total = categories.reduce((n, c) => n + c.links.length, 0);

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-6 sm:py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-fg">Resources</h1>
          <p className="mt-1 text-sm text-muted">Lab handbooks, genetics resources, forms and contacts. Links open in a new tab.</p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-faint" aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter links…"
            aria-label="Filter links"
            className="field h-10 pl-9"
          />
        </div>
      </div>

      <p className="sr-only" aria-live="polite">{query ? `${total} links match` : ''}</p>

      {categories.length === 0 ? (
        <EmptyState icon={SearchX} title="No links match" className="mt-6">Try a lab name or a word like “forms”.</EmptyState>
      ) : (
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          {categories.map((cat) => (
            <section key={cat.category} aria-labelledby={`cat-${cat.category}`} className="rounded-2xl border border-line bg-surface">
              <h2 id={`cat-${cat.category}`} className="border-b border-line px-5 py-3 text-sm font-semibold text-fg">
                {cat.category}
                <span className="ml-2 text-xs font-normal text-muted">{cat.links.length}</span>
              </h2>
              <ul className="divide-y divide-line/70">
                {cat.links.map((link) => {
                  const intranet = isIntranet(link.url);
                  return (
                    <li key={`${link.name}-${link.url}`}>
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        referrerPolicy="no-referrer"
                        className="group flex items-center gap-3 px-5 py-2.5 transition-colors hover:bg-raised/60"
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm text-fg group-hover:text-accent-fg">{link.name}</span>
                          <span className="block truncate text-xs text-muted">{hostOf(link.url)}</span>
                        </span>
                        {intranet && <Badge icon={Lock} title="Only works on the hospital network">Intranet</Badge>}
                        {isPdf(link.url) && <Badge icon={FileText}>PDF</Badge>}
                        <ArrowUpRight size={15} className="shrink-0 text-faint group-hover:text-accent-fg" aria-hidden="true" />
                        <span className="sr-only">(opens in a new tab)</span>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
